import { StoreSubject } from "../../../../src/lib/store";
import {
  cloneInstrument,
  clonePreset,
  cloneSlotMixes,
  createDefaultInstruments,
  createDefaultSlotMixes,
  createDefaultSlotFxStacks,
  createEmptyClip,
  defaultPresetForSlot,
  instrumentsFromPresets,
  normalizeSlotMixes,
  normalizeSlotFxStacks,
  cloneSlotFxStacks,
  presetsFromInstruments,
  synthInstrument,
  type ClipIR,
  type FxNode,
  type Instrument,
  type SlotMix,
  type SynthPreset,
  DEFAULT_INSTRUMENT_SLOT_COUNT,
} from "../../../../src/lib/audio";
import { createDefaultBank, type NamedPreset } from "../lib/presetBank";
import { createDefaultDrumBank, type NamedDrumKit } from "../lib/drumKitBank";
import { createDefaultWavetableBank, type NamedWavetablePreset } from "../lib/wavetableBank";
import { createDefaultSamplerBank, type NamedSamplerPreset } from "../lib/samplerBank";
import { createDefaultMuteSolo, normalizeMuteSoloFlags } from "../lib/muteSolo";
import {
  createDefaultTrackColors,
  normalizeTrackColors,
  type TrackColor,
} from "../lib/trackColors";

export type LabProjectState = {
  clip: ClipIR;
  /** Source of truth for slot payloads (synth or drums). */
  instruments: Instrument[];
  /**
   * Synth-preset mirror for bank ops / demos. Drum slots use the factory
   * synth default for that index so bank code can stay preset-shaped.
   */
  presets: SynthPreset[];
  bank: NamedPreset[];
  /** Named drum-kit library (factory Studio Kit first). */
  drumBank: NamedDrumKit[];
  /** Named wavetable preset library. */
  wavetableBank: NamedWavetablePreset[];
  /** Named sampler preset library (sample ids + settings, not PCM). */
  samplerBank: NamedSamplerPreset[];
  /** Per-slot volume + pan for the channel strip / kernel. */
  slotMix: SlotMix[];
  /** Per-slot insert FX stacks (mono, pre-pan). */
  slotFx: FxNode[][];
  /** Tracker mute flags (length = instruments.length). */
  muted: boolean[];
  /** Tracker solo flags (length = instruments.length). */
  solo: boolean[];
  /** Project-global track header/column tints (`null` = unset). */
  trackColors: TrackColor[];
  fx: FxNode[];
};

const initialInstruments = createDefaultInstruments();

const initial: LabProjectState = {
  clip: createEmptyClip({ bpm: 120, steps: 16, stepDivision: 16 }),
  instruments: initialInstruments,
  presets: presetsFromInstruments(initialInstruments),
  bank: createDefaultBank(),
  drumBank: createDefaultDrumBank(),
  wavetableBank: createDefaultWavetableBank(),
  samplerBank: createDefaultSamplerBank(),
  slotMix: createDefaultSlotMixes(),
  slotFx: createDefaultSlotFxStacks(),
  muted: createDefaultMuteSolo(),
  solo: createDefaultMuteSolo(),
  trackColors: createDefaultTrackColors(),
  fx: [],
};

/**
 * Document of record for React panels (clip, instruments, bank, fx).
 * `labRuntime` still owns live tracker buffers and pushes coarse snapshots here.
 */
export const projectStore = new StoreSubject<LabProjectState>(initial);

/** Merge a presets[] write into instruments without clobbering drum/sf2 slots. */
export function mergePresetsIntoInstruments(
  instruments: Instrument[],
  presets: SynthPreset[],
): Instrument[] {
  const n = Math.max(
    instruments.length,
    presets.length,
  );
  return Array.from({ length: n }, (_, i) => {
    const inst = instruments[i];
    if (inst?.kind === "drums" || inst?.kind === "sf2" || inst?.kind === "wavetable" || inst?.kind === "sampler") {
      return cloneInstrument(inst);
    }
    const preset = presets[i] ?? defaultPresetForSlot(i);
    return synthInstrument(preset);
  });
}

export function getSynthPreset(
  state: LabProjectState,
  slot: number,
): SynthPreset | undefined {
  const instrument = state.instruments[slot];
  if (instrument?.kind === "synth") {
    return instrument.preset;
  }
  return state.presets[slot];
}

export function patchProject(partial: Partial<LabProjectState>): void {
  const current = projectStore.getValue();
  const next: LabProjectState = { ...current, ...partial };

  // Tracker mutates clip in place; always snapshot so React selectors (Object.is
  // on `state.clip`) see a new reference after mute / edits.
  if (partial.clip) {
    next.clip = structuredClone(partial.clip);
  }

  if (partial.instruments) {
    next.instruments = partial.instruments.map(cloneInstrument);
    next.presets = presetsFromInstruments(next.instruments);
  } else if (partial.presets) {
    next.presets = partial.presets.map(clonePreset);
    next.instruments = mergePresetsIntoInstruments(current.instruments, next.presets);
  }

  if (partial.drumBank) {
    next.drumBank = partial.drumBank;
  }
  if (partial.wavetableBank) {
    next.wavetableBank = partial.wavetableBank;
  }
  if (partial.samplerBank) {
    next.samplerBank = partial.samplerBank;
  }

  const trackCount = next.instruments.length || DEFAULT_INSTRUMENT_SLOT_COUNT;

  if (partial.slotMix) {
    next.slotMix = cloneSlotMixes(normalizeSlotMixes(partial.slotMix, trackCount));
  } else if (next.slotMix.length !== trackCount) {
    next.slotMix = cloneSlotMixes(normalizeSlotMixes(next.slotMix, trackCount));
  }

  if (partial.slotFx) {
    next.slotFx = cloneSlotFxStacks(normalizeSlotFxStacks(partial.slotFx, trackCount));
  } else if (next.slotFx.length !== trackCount) {
    next.slotFx = cloneSlotFxStacks(normalizeSlotFxStacks(next.slotFx, trackCount));
  }

  if (partial.muted) {
    next.muted = normalizeMuteSoloFlags(partial.muted, trackCount);
  } else if (next.muted.length !== trackCount) {
    next.muted = normalizeMuteSoloFlags(next.muted, trackCount);
  }

  if (partial.solo) {
    next.solo = normalizeMuteSoloFlags(partial.solo, trackCount);
  } else if (next.solo.length !== trackCount) {
    next.solo = normalizeMuteSoloFlags(next.solo, trackCount);
  }

  if (partial.trackColors) {
    next.trackColors = normalizeTrackColors(partial.trackColors, trackCount);
  } else if (next.trackColors.length !== trackCount) {
    next.trackColors = normalizeTrackColors(next.trackColors, trackCount);
  }

  projectStore.next(next);
}

/** Convenience: replace one slot instrument and sync presets mirror. */
export function patchInstrument(slot: number, instrument: Instrument): void {
  const { instruments } = projectStore.getValue();
  const next = instruments.map((inst, i) => (i === slot ? cloneInstrument(instrument) : inst));
  patchProject({ instruments: next });
}

/** Bootstrap helpers for load paths that still only have presets. */
export function instrumentsFromLegacyPresets(presets: SynthPreset[]): Instrument[] {
  return instrumentsFromPresets(presets);
}
